import { getAddressInfo, AddressType } from "bitcoin-address-validation";
import { MessageSigningProtocols } from "@sats-connect/core";

import { Buff } from "@cmdcode/buff";
import { BaseUtxo, OracleAPI, SignPSBTEntry, Transaction, VaultWallet, WalletAccountRecord } from "@ducat-unit/client-sdk";
import { PSBT, TX } from "@ducat-unit/client-sdk/util";
import { FunctionVoid, WALLET_CONNECTOR, WalletIsValidResult, TransferResult } from "./connector";
import { hexToBase64 } from "./util";
import { toast } from "sonner";
import { Network } from "./networkConfig";
import { Logger } from '@/constants';
import { WalletInfo } from "@/context/types";

/**
 * Unisat wallet docs: https://docs.unisat.io/dev/unisat-developer-center/unisat-wallet
 */
// unisat.requestAccounts(network)
// unisat.getPublicKey()
// unisat.getBalance()
// unisat.getChain()
// unisat.switchChain(chain)

// Listeners:
// unisat.on('accountsChanged', handler: (accounts: Array<string>) => void);
// unisat.removeListener('accountsChanged', handler: (accounts: Array<string>) => void);
// unisat.on('networkChanged', handler: (network: string) => void);
// unisat.removeListener('networkChanged', handler: (network: string) => void);

const logger = Logger('lib/connector/unisat')

function computeAccountRecord_unisat(address: string, pk: string): WalletAccountRecord {
  const addressType = getAddressInfo(address).type;
  const pubkey = addressType === AddressType.p2tr ? pk.slice(2) : pk;
  return {
    sats: { address, pubkey },
    runes: { address, pubkey },
    vault: { address, pubkey },
  };
}

async function get_wallet_accounts_unisat(): Promise<WalletAccountRecord> {
  if (!(window as any).unisat) {
    toast("Please Install Unisat Wallet to Continue.");
    throw Error(`wallet not installed`);
  }
  const unisat = (window as any).unisat;
  const accounts = await unisat.requestAccounts();
  const pk = await unisat.getPublicKey();
  const address = accounts[0];

  return computeAccountRecord_unisat(address, pk);
}

async function reconnect(walletInfo: WalletInfo) {
  if (!(window as any).unisat) {
    toast('Please Install Unisat Wallet to Continue.');
    throw Error(`wallet not installed`);
  }
  const unisat = (window as any).unisat;
  const accounts = await unisat.getAccounts();
  const address = accounts[0];
  if (address !== walletInfo?.address) {
    throw Error('Expected different wallet address to be connected');
  }
  const pk = await unisat.getPublicKey();

  return computeAccountRecord_unisat(accounts[0], pk);
}

// utils from sat-connect/core
type InputType = {
  index: number;
  address?: string;
  publicKey?: string;
  sighashTypes?: number[];
  // See: https://github.com/unisat-wallet/wallet-utils/blob/master/src/types.ts#L28
  useTweakedSigner?: boolean;
}[];
function convertSignInputsToInputType (
  psbt       : string,
  signInputs : Record<string, number[]>
): InputType {
  const pdata = PSBT.decode(psbt)

  let result: InputType = [];

  for (let address in signInputs) {
    let indexes = signInputs[address];
    for (let index of indexes) {
      const pinput     = pdata.getInput(index)
      const addr_data  = TX.parse_address(address)
      const tapscript  = pinput.tapLeafScript?.at(0)

      const is_p2tr_keyspend = (addr_data.type === 'p2tr' && tapscript === undefined)

      result.push({
        index: index,
        address: address,
        useTweakedSigner : is_p2tr_keyspend
      });
    }
  }
  return result;
}

const get_sats_utxos_api = (client: VaultWallet) => async () : Promise<BaseUtxo[]> => {
  logger.debug('get unisat sats utxos')
  const addr = client.acct.runes.address;
  const host = client.config.indexer;
  return fetchSatsUtxosUnisat(host.ord, addr);
};

export async function fetchSatsUtxosUnisat(ordUrl: string, addr: string) {
  const res = await OracleAPI.wallet.fetch_rune_utxos(ordUrl, addr);
  if (!res.ok) throw new Error(res.error);
  logger.debug(res.data)
  const utxos = Array.from(res.data.values())
  const filtered = utxos.filter(e => (
    e.records.length === 0 && e.runes.size === 0
  )).map(({ script, txid, value, vout }) => ({ script, txid, value, vout }))
  return filtered
}

const sign_psbt_api = (client: VaultWallet) =>
  async (
    psbt: string,
    signInputs: Record<string, number[]>,
  ): Promise<string> => {
    if (!(window as any).unisat) {
      toast("Please Install Unisat Wallet to Continue.");
      throw Error(`wallet not installed`);
    }
    
    const toSignInputs = convertSignInputsToInputType(psbt, signInputs);
    console.log(`- toSignInputs:`, toSignInputs);

    const unisat = (window as any).unisat;

    const pdata = PSBT.decode(psbt);
    console.log(`- psbt and pdata`, psbt, pdata);

    let resPsbt;
    
    try {
      const psbtHex = Buffer.from(psbt, "base64").toString("hex");
      resPsbt = await unisat.signPsbt(psbtHex, {
        toSignInputs,
        autoFinalized: false,
      });
    } catch (e) {
      console.log(`[unisat.signPsbt] error:`, e);
      throw e;
    }
    // console.log(`Signed psbt hex:`, resPsbt);
    const resPsbtBase64 = hexToBase64(resPsbt);
    const pdataRes = PSBT.decode(resPsbtBase64);

    // Clean up after unisat wallet.
    cleanupSignedPsbtUnisat(client, pdataRes);

    // console.log(`> Final: `, PSBT.encode(pdataRes));

    return PSBT.encode(pdataRes);
  };

function cleanupSignedPsbtUnisat(client: VaultWallet, pdataRes: Transaction) {
  // Clean up after unisat wallet.
  for (let i = 0; i < pdataRes.inputsLength; i++) {
    // Fetch psbt input.
    const pvin = pdataRes.getInput(i);
    // If witness is undefined:
    if (pvin.finalScriptWitness === undefined) {
      // If a taproot key-spend has been defined:
      if (pvin.tapKeySig !== undefined) {
        // Set the final witness as the signature.
        // Update the psbt input with the final witness.
        pdataRes.updateInput(i, { finalScriptWitness: [pvin.tapKeySig] });
      }
    } else {
      // Check the first element of the witness.
      const elem = pvin.finalScriptWitness.at(0);
      // If the witness is empty, skip.
      if (elem === undefined) continue;
      // Convert the element to a hex string.
      const elem_hex = new Buff(elem).hex;
      // Define the vault pubkey for the wallet.
      const vault_pk = client.acct.vault.pubkey;
      // If the element hex matches the vault pubkey:
      if (elem_hex === vault_pk) {
        // Remove the erroneous pubkey!
        const wit = pvin.finalScriptWitness.slice(1);
        // Update the psbt input.
        pdataRes.updateInput(i, { finalScriptWitness: wit });
      }
    }
  }
}

async function sign_message({
  message,
  address,
}: {
  address: string;
  message: string;
}) {
  // For taproot address bip3222 should be used instead of ecdsa.
  // See: https://github.com/secretkeylabs/sats-connect-core/blob/main/src/adapters/unisat.ts#L100
  const response = await (window as any).unisat.signMessage(
    message,
    "bip322-simple"
  );
  return {
    status: "success",
    result: {
      address,
      messageHash: "",
      signature: response,
      protocol: MessageSigningProtocols.BIP322,
    },
  };
}

function disconnect() {
  return (window as any).unisat.disconnect();
}

// https://docs.unisat.io/dev/open-api-documentation/unisat-wallet#sendbitcoin
const sendTransfer = async ({ address, amountSats, feeRate }: { amountSats: number; address: string; feeRate?: number }): Promise<TransferResult> => {
    try {
    type UnisatWindow = { unisat: { sendBitcoin: (address: string, amount: number, options?: { feeRate?: number }) => Promise<string> } };
    const unisat = (window as unknown as UnisatWindow).unisat;
    const options = feeRate ? { feeRate } : undefined;
    const txid = await unisat.sendBitcoin(address, amountSats, options);

    return { error: false, txid };
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    console.error('sendTransfer (Unisat) error:', err);

    return { error: true, message: errorMessage };
  }
};

// https://docs.unisat.io/dev/open-api-documentation/unisat-wallet#pushpsbt
const broadcastPsbt = async ({ signedPsbtHex }: { signedPsbtHex: string }): Promise<TransferResult> => {
  try {
    const txid = await (window as any).unisat.pushPsbt(signedPsbtHex);

    return { error: false, txid };
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    console.error('broadcastPsbt (Unisat) error:', err);

    return { error: true, message: errorMessage };
  }
};

// const signPsbt = async (psbt: string, signInputs: Record<string, number[]>, broadcast: boolean): Promise<string> => {
//   try {
//     const toSignInputs = convertSignInputsToInputType(psbt, signInputs);
//     console.log(`- toSignInputs:`, toSignInputs);
//     const psbtHex = Buffer.from(psbt, "base64").toString("hex");
//     const result = await (window as any).unisat.signPsbt(psbtHex, {
//       toSignInputs,
//       autoFinalized: broadcast,
//     });
//     return result;
//   } catch (err: unknown) {
//     const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
//     console.error('signPsbt (Unisat) error:', err);
//     throw errorMessage;
//   }
// };

type UNISAT_SUPPORTED_CHAINS =
  | "BITCOIN_MAINNET"
  | "BITCOIN_TESTNET"
  | "BITCOIN_TESTNET4"
  | "BITCOIN_SIGNET"
  | "FRACTAL_BITCOIN_MAINNET"
  | "FRACTAL_BITCOIN_TESTNET";
type UNISAT_SUPPORTED_NETWORKS = "mainnet" | "signet";

async function switchNetwork(network: Network) {
  const supportedNetworks: Record<
    UNISAT_SUPPORTED_NETWORKS,
    UNISAT_SUPPORTED_CHAINS
  > = {
    mainnet: "BITCOIN_MAINNET",
    signet: "BITCOIN_SIGNET",
  };
  if (supportedNetworks[network as UNISAT_SUPPORTED_NETWORKS]) {
    await (window as any).unisat.switchNetwork(network);
    return true;
  }
}

// Docs: https://docs.unisat.io/dev/unisat-developer-center/unisat-wallet#events
const subscribe = (event: 'accountsChanged' | 'networkChanged') => (onChange: FunctionVoid) => {
  const unisat = (window as any).unisat;
  unisat.on(event, onChange);
  return () => unisat.removeListener(event, onChange);
}

// Docs: https://docs.unisat.io/dev/unisat-developer-center/unisat-wallet#signpsbts
const sign_batch = (client: VaultWallet) => async (psbts: SignPSBTEntry[]): Promise<string[]> => {
  const unisat = (window as any).unisat;
  console.log(`UNISAT [sign_batch] psbts:`, psbts);

  const [psbt1, signInputs1] = psbts[0];
  const [psbt2, signInputs2] = psbts[1];

  // Prepare psbts and options:
  const psbtHex1 = Buffer.from(psbt1, "base64").toString("hex");
  const psbtHex2 = Buffer.from(psbt2, "base64").toString("hex");
  const toSignInputs1 = convertSignInputsToInputType(psbt1, signInputs1);
  const toSignInputs2 = convertSignInputsToInputType(psbt2, signInputs2);

  // Sign both:
  const result = await unisat.signPsbts([
    psbtHex1,
    psbtHex2
  ], [{
    toSignInputs: toSignInputs1,
    autoFinalized: false,
  },{
    toSignInputs: toSignInputs2,
    autoFinalized: false,
  }]);
  const [signedHex1, signedHex2] = result;

  const signedBase64_1 = hexToBase64(signedHex1);
  const signedBase64_2 = hexToBase64(signedHex2);

  const post_pdata1 = PSBT.decode(signedBase64_1);
  const post_pdata2 = PSBT.decode(signedBase64_2);

  // Clean up after unisat wallet.
  cleanupSignedPsbtUnisat(client, post_pdata1);
  cleanupSignedPsbtUnisat(client, post_pdata2);

  return [
    PSBT.encode(post_pdata1),
    PSBT.encode(post_pdata2),
  ];
};

function isValidWalletAccountRecord_unisat(walletAccountRecord: WalletAccountRecord): WalletIsValidResult {
  const satsAddress = walletAccountRecord.sats.address;
  const addressType = getAddressInfo(satsAddress).type;

  if (addressType !== AddressType.p2tr) {
    return { isValid: false, errorMessage: 'Please use Taproot address for your Unisat wallet.' };
  }
  return { isValid: true, errorMessage: '' };
}

export const UNISAT_CONNECTOR = {
  api: {
    ...WALLET_CONNECTOR.api,

    // Override the default connector methods as needed.
    fetch: {
      ...WALLET_CONNECTOR.api.fetch,
      sats_utxos: get_sats_utxos_api,
    },

    sign: {
      ...WALLET_CONNECTOR.api.sign,
      psbt: sign_psbt_api,
      batch : sign_batch,
    },
  },

  // Extra methods related to specific wallet (not related to client-sdk)
  getWalletAccounts: get_wallet_accounts_unisat,
  reconnect,
  signMessage: sign_message,
  sendTransfer,
  broadcastPsbt,
  disconnect,
  switchNetwork,
  subscribe: {
    accountChange: subscribe('accountsChanged'),
    networkChange: subscribe('networkChanged'),
  },
  isValidWalletAccountRecord: isValidWalletAccountRecord_unisat,
}
